import Delete from '@mui/icons-material/Delete';
import { Box } from '@mui/material';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { API_SERVER, IDENTITY_API_SERVER } from 'modules/common/api';
import IconButtonTitle from 'modules/common/component/IconButtonTitle';
import LoadingIcon from 'modules/common/component/LoadingIcon';
import TableCustom from 'modules/common/component/TableCustom';
import useGeneralHook from 'modules/common/hook/useGeneralHook';
import SchemaForm from 'modules/common/SchemaForm';
import { ROLE_INFO } from 'modules/layout/utils';
import { useFetch, useCreateMutate, useDeleteMutate } from 'modules/common/hook';
import { IndicationRequest } from 'modules/schema';
import { keyBy, sortBy } from 'lodash';

interface Props {
  data?: IndicationRequest;
  readOnly?: boolean;
}

const PARTICIPANTS = [ROLE_INFO.examDoctor, ROLE_INFO.labDoctor, ROLE_INFO.laboratory, ROLE_INFO.nurse];

const ParticipantTab: React.FunctionComponent<Props> = ({ data, readOnly }) => {
  const { confirmDialog, intl } = useGeneralHook();

  // get list users by role
  const { data: users } = useFetch(
    IDENTITY_API_SERVER.user.getListUserByRoles({
      roles: PARTICIPANTS.map((item) => item.roleKey).join(','),
      status: 'ACTIVE',
    }),
  );

  // get list participating personnel
  const { data: participants, revalidate: participantsRevalidate } = useFetch(
    API_SERVER.indicationAttendPerson.get(data?.id || 0),
    { enabled: data?.id },
  );

  // map list users on field
  const participantOptions = React.useMemo(() => {
    if (users && users.length > 0) {
      const roleMap = keyBy(PARTICIPANTS, 'roleKey');
      return sortBy(
        users
          ?.filter((item) => item?.practiceCertificate)
          ?.map((item) => {
            return {
              id: item?.id,
              name: item?.employeeFullName,
              roleName: item?.roleName,
              group: intl.formatMessage({ id: item?.roleName && roleMap[item?.roleName]?.label }),
            };
          }),
        'group',
      );
    }
    return [];
  }, [intl, users]);

  // map list participating personnel
  const participantsMapped = React.useMemo(() => {
    return participants?.map((person) => ({
      ...person,
      roleLabel: intl.formatMessage({
        id: PARTICIPANTS.find((item) => item?.roleKey === person?.roleName)?.label || ' ',
      }),
    }));
  }, [intl, participants]);

  // create participating personnel
  const addParticipantMutate = useCreateMutate({ onSuccess: participantsRevalidate });
  const addParticipant = React.useCallback(
    async (value) => {
      const dataAdd = {
        indicationRequestId: data?.id,
        roleName: value?.roleName,
        userId: value?.id,
        employeeName: value?.name,
      };

      await addParticipantMutate({
        url: API_SERVER.indicationAttendPerson.create(),
        method: 'post',
        data: dataAdd,
      });
    },
    [addParticipantMutate, data?.id],
  );

  // delete participating personnel
  const deletePerformerMutate = useDeleteMutate({ onSuccess: participantsRevalidate });
  const deletePerformer = React.useCallback(
    async (value) => {
      const confirm = await confirmDialog.promptConfirmation({
        warning: true,
        title: intl.formatMessage({ id: 'confirmDeleteTitle' }),
        content: intl.formatMessage({ id: 'confirmDelete' }, { name: value.name }),
      });
      if (confirm) {
        await deletePerformerMutate({
          url: API_SERVER.indicationAttendPerson.delete(value?.id),
          method: 'delete',
        });
      }
      confirmDialog.close();
    },
    [confirmDialog, deletePerformerMutate, intl],
  );

  if (!data) {
    return <LoadingIcon />;
  }

  return (
    <>
      {!readOnly && (
        <SchemaForm
          hideSubmitButton
          submitLabel={<FormattedMessage id="status.finished" />}
          schema={{
            fields: ({ formProps: { intl }, valuesField }) => {
              const nameOptions = valuesField?.role
                ? participantOptions?.filter((item) => item?.roleName === valuesField?.role?.roleKey)
                : participantOptions;
              return {
                role: {
                  type: 'auto-complete',
                  label: intl.formatMessage({ id: 'role' }),
                  placeholder: intl.formatMessage({ id: 'roleEnter' }),
                  options: PARTICIPANTS,
                  getOptionLabel: (role) => {
                    return intl.formatMessage({ id: role.label });
                  },
                  propsWrapper: { xs: 5 },
                },
                name: {
                  type: 'auto-complete',
                  label: intl.formatMessage({ id: 'employeeName' }),
                  placeholder: intl.formatMessage({ id: 'employeeEnter' }),
                  propsWrapper: { xs: 7 },
                  options: nameOptions,
                  groupBy: (option) => option.group,
                  getOptionLabel: (opt) => {
                    return opt.name;
                  },
                  getOptionDisabled: (opt) => {
                    return !!participantsMapped?.find((item) => item?.userId === opt?.id);
                  },
                  onChange: (v) => {
                    if (!!v) {
                      addParticipant(v);
                    }
                  },
                },
              };
            },
          }}
        />
      )}
      <Box marginTop={1}>
        <TableCustom
          dataSource={participantsMapped}
          columns={[
            {
              title: 'role',
              dataIndex: 'roleLabel',
              width: '30%',
            },
            {
              title: 'employeeName',
              dataIndex: 'employeeName',
            },
            {
              title: 'action',
              align: 'right',
              hidden: readOnly,
              render: (record) => (
                <>
                  <IconButtonTitle title="delete" size="small" onClick={() => deletePerformer(record)}>
                    <Delete />
                  </IconButtonTitle>
                </>
              ),
            },
          ]}
        />
      </Box>
    </>
  );
};

export default ParticipantTab;
